import React, { Component } from 'react';
import _ from 'lodash';
import {
  Typography,
  TextField,
  MenuItem,
  Paper,
  Chip,
  Icon,
  withStyles,
  withTheme,
} from '@material-ui/core';
import { emphasize } from '@material-ui/core/styles/colorManipulator';
import AsyncSelect from 'react-select/lib/Async';
import PropTypes from 'prop-types';
import classNames from 'classnames';

const styles = theme => ({
  root: {
    flexGrow: 1,
    height: 250,
  },
  input: {
    display: 'flex',
    padding: 0,
  },
  valueContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    flex: 1,
    alignItems: 'center',
    overflow: 'hidden',
    marginTop: '6px',
  },
  chip: {
    margin: `${theme.spacing.unit / 2}px ${theme.spacing.unit / 4}px`,
  },
  chipFocused: {
    backgroundColor: emphasize(
      theme.palette.type === 'light'
        ? theme.palette.grey[300]
        : theme.palette.grey[700],
      0.08,
    ),
  },
  noOptionsMessage: {
    padding: `${theme.spacing.unit}px ${theme.spacing.unit * 2}px`,
  },
  singleValue: {
    fontSize: 16,
  },
  placeholder: {
    position: 'absolute',
    left: 2,
    fontSize: 16,
  },
  paper: {
    position: 'absolute',
    zIndex: 1,
    marginTop: theme.spacing.unit,
    left: 0,
    right: 0,
  },
  divider: {
    height: theme.spacing.unit * 2,
  },
});

const NoOptionsMessage = props => (
  <Typography
    color="textSecondary"
    className={props.selectProps.classes.noOptionsMessage}
    {...props.innerProps}
  >
    {props.children}
  </Typography>
);
NoOptionsMessage.propTypes = {
  selectProps: PropTypes.shape(PropTypes.any),
  innerProps: PropTypes.shape(PropTypes.any),
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.shape(PropTypes.any)]),
};
NoOptionsMessage.defaultProps = {
  selectProps: {},
  innerProps: {},
  children: {},
};

const inputComponent = ({ inputRef, ...props }) => (
  <div ref={inputRef} {...props} />
);
inputComponent.propTypes = {
  inputRef: PropTypes.shape(PropTypes.any),
};
inputComponent.defaultProps = {
  inputRef: null,
};

const Control = props => (
  <TextField
    fullWidth
    InputProps={{
      inputComponent,
      inputProps: {
        className: props.selectProps.classes.input,
        inputRef: props.innerRef,
        children: props.children,
        ...props.innerProps,
      },
    }}
    {...props.selectProps.textFieldProps}
  />
);
Control.propTypes = {
  selectProps: PropTypes.shape(PropTypes.any),
  innerProps: PropTypes.shape(PropTypes.any),
  children: PropTypes.shape(PropTypes.any),
  innerRef: PropTypes.shape(PropTypes.any),
};
Control.defaultProps = {
  selectProps: {},
  innerProps: {},
  children: {},
  innerRef: null,
};

const Option = props => (
  <MenuItem
    buttonRef={props.innerRef}
    selected={props.isFocused}
    component="div"
    style={{
      fontWeight: props.isSelected ? 500 : 400,
    }}
    {...props.innerProps}
  >
    {props.children}
  </MenuItem>
);
Option.propTypes = {
  isFocused: PropTypes.bool,
  isSelected: PropTypes.bool,
  innerProps: PropTypes.shape(PropTypes.any),
  children: PropTypes.shape(PropTypes.any),
  innerRef: PropTypes.shape(PropTypes.any),
};
Option.defaultProps = {
  isFocused: false,
  isSelected: false,
  innerProps: {},
  children: {},
  innerRef: null,
};

const Placeholder = props => (
  <Typography
    color="textSecondary"
    className={props.selectProps.classes.placeholder}
    {...props.innerProps}
  >
    {props.children}
  </Typography>
);
Placeholder.propTypes = {
  selectProps: PropTypes.shape(PropTypes.any),
  innerProps: PropTypes.shape(PropTypes.any),
  children: PropTypes.shape(PropTypes.any),
};
Placeholder.defaultProps = {
  selectProps: {},
  innerProps: {},
  children: undefined,
};

function SingleValue(props) {
  return (
    <Typography
      className={props.selectProps.classes.singleValue}
      {...props.innerProps}
    >
      {props.children}
    </Typography>
  );
}
SingleValue.propTypes = {
  selectProps: PropTypes.shape(PropTypes.any),
  innerProps: PropTypes.shape(PropTypes.any),
  children: PropTypes.shape(PropTypes.any),
};
SingleValue.defaultProps = {
  selectProps: {},
  innerProps: {},
  children: {},
};

const ValueContainer = props => (
  <div className={props.selectProps.classes.valueContainer}>
    {props.children}
  </div>
);
ValueContainer.propTypes = {
  selectProps: PropTypes.shape(PropTypes.any),
  children: PropTypes.shape(PropTypes.any),
};
ValueContainer.defaultProps = {
  selectProps: {},
  children: {},
};

const MultiValue = props => (
  <Chip
    tabIndex={-1}
    label={props.children}
    className={classNames(props.selectProps.classes.chip, {
      [props.selectProps.classes.chipFocused]: props.isFocused,
    })}
    onDelete={props.removeProps.onClick}
    deleteIcon={<Icon {...props.removeProps}>close</Icon>}
  />
);
MultiValue.propTypes = {
  selectProps: PropTypes.shape(PropTypes.any),
  children: PropTypes.shape(PropTypes.any),
  isFocused: PropTypes.bool,
  removeProps: PropTypes.shape(PropTypes.any),
};
MultiValue.defaultProps = {
  selectProps: {},
  children: {},
  isFocused: false,
  removeProps: {},
};

const Menu = props => (
  <Paper
    square
    className={props.selectProps.classes.paper}
    {...props.innerProps}
  >
    {props.children}
  </Paper>
);
Menu.propTypes = {
  selectProps: PropTypes.shape(PropTypes.any),
  innerProps: PropTypes.shape(PropTypes.any),
  children: PropTypes.shape(PropTypes.any),
};
Menu.defaultProps = {
  selectProps: {},
  innerProps: {},
  children: {},
};

const DropdownIndicator = () => <Icon>arrow_drop_down</Icon>;

const components = {
  Control,
  Menu,
  MultiValue,
  NoOptionsMessage,
  // Option,
  Placeholder,
  SingleValue,
  ValueContainer,
  DropdownIndicator,
};

class FilterableSelect extends Component {
  state = {
    open: false,
  };

  loadOptionsDebounced = _.debounce(async (inputValue, callback) => {
    const result = await this.props.onLazyLoad(inputValue);
    callback(result);
  }, 500);

  onBlur = () => {
    this.setState({ open: false });
  };

  onFocus = () => {
    this.setState({
      open: true,
    });
  };

  filterOptions = (inputValue) => {
    const { options, disableDefaultOptions } = this.props;

    if (disableDefaultOptions && !inputValue) {
      return [];
    }

    if (!inputValue || !options) {
      return options;
    }

    return options.filter(item => item.label
      .toLowerCase().indexOf(inputValue.toLowerCase()) >= 0);
  };

  loadOptions = (inputValue, callback) => {
    if (this.props.onLazyLoad && typeof this.props.onLazyLoad === 'function') {
      this.loadOptionsDebounced(inputValue, callback);
    } else if (this.props.onTextChange && typeof this.props.onTextChange === 'function') {
      this.props.onTextChange(inputValue, callback);
    } else {
      callback(this.filterOptions(inputValue));
    }
  };

  render() {
    const {
      classes,
      theme,
      options,
      label,
      input: {
        value, onChange, onBlur,
      },
      required,
      onValueChange,
      disabled,
      onLazyLoad,
      disableDefaultOptions,
    } = this.props;

    const selectStyles = {
      input: base => ({
        ...base,
        color: theme.palette.text.primary,
        '& input': {
          font: 'inherit',
        },
      }),
    };

    const selectedOption = options.filter(option => option.value === value);

    return (
      <AsyncSelect
        required={required}
        classes={classes}
        styles={selectStyles}
        cacheOptions={!onLazyLoad}
        defaultOptions={disableDefaultOptions ? [] : options}
        loadOptions={this.loadOptions}
        components={components}
        value={selectedOption}
        onChange={(option) => {
          onChange(option && option.value);
          onValueChange(option && option.value);
        }}
        onBlur={() => {
          this.onBlur();
          if (onBlur) {
            onBlur(value);
          }
        }}
        onFocus={this.onFocus}
        textFieldProps={{
          label: label + (required ? ' *' : ''),
          InputLabelProps: {
            shrink: !!(selectedOption.length > 0 || this.state.open),
          },
        }}
        menuPosition="fixed"
        placeholder=""
        isClearable
        isDisabled={disabled}
      />
    );
  }
}

FilterableSelect.propTypes = {
  classes: PropTypes.shape(PropTypes.any),
  theme: PropTypes.shape(PropTypes.any),
  options: PropTypes.arrayOf(PropTypes.any),
  label: PropTypes.string,
  input: PropTypes.shape(PropTypes.any),
  required: PropTypes.bool,
  disableDefaultOptions: PropTypes.bool,
  onValueChange: PropTypes.func,
  onLazyLoad: PropTypes.func,
  onTextChange: PropTypes.func,
};

FilterableSelect.defaultProps = {
  classes: {},
  theme: {},
  options: [],
  label: '',
  input: {},
  required: false,
  disableDefaultOptions: false,
  onValueChange: () => {},
  onLazyLoad: undefined,
  onTextChange: undefined,
};

const FilterableSelectWithStyles = withStyles(styles)(FilterableSelect);

export default withTheme()(FilterableSelectWithStyles);
